import { useQueryClient } from '@tanstack/react-query';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { IMenuItem } from 'src/types/menu.types';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bin,
  IOption,
  Card as ItemCard,
  LinkIcon,
  MoveCategoryIcon,
  PrimaryButton,
  Prompt,
  SaladIcon,
  SnackbarStatus,
  useSnackbar,
  ViewIcon,
} from '@weareopenr/component-library';
import { useMenuTray } from 'src/contexts/menuTray';
import { MoveCategoryTrayContent } from '../MoveCategoryTray';
import { memo, useState } from 'react';
import { ProductPreviewTrayContent } from '../ProductPreviewTray';
import { deleteMenuItem } from 'src/api/deleteMenuItem/deleteMenuItem';
import { reorderMenuItem } from 'src/api/reorderMenuItem/reorderMenuItem';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { getNextOpenId } from 'src/helpers/getNextOpenId';

export interface IMenuItemCardProps extends IMenuItem {
  menuId: string;
  parentItemId: string;
  sectionId?: string;
  displayOrderLeft?: number;
  displayOrderRight?: number;
  // Read-only menus (e.g. within a menu set) can only view the product, so the menu isn't needed.
  isViewOnly?: boolean;
}

const MenuItemCard = ({
  menuId,
  name,
  image,
  itemId,
  parentItemId,
  displayOrderLeft,
  displayOrderRight,
  sectionId,
  consumerName,
  isViewOnly,
}: IMenuItemCardProps) => {
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { showSnackbar } = useSnackbar();
  const { openTray, closeTray, trayKind, isTrayOwnedBy, setTrayDirty } = useMenuTray();
  const queryClient = useQueryClient();

  const { data: currentMenu, isFetching } = useMenu(menuId, { enabled: !isViewOnly });

  const removePromptTitle = `Are you sure you want to remove "${name}"?`;
  const removeItemPromptSubTitle = `You will remove this product from the current menu.`;
  const isModifier = currentMenu && currentMenu.modifiers.find((modifier) => modifier.itemId === itemId);
  const multipleSections = currentMenu?.sections.length > 0;
  const redirectPath = `/items/${parentItemId}`;
  const isPartOfBundle = (): boolean => {
    let isWithinBundle = false;
    currentMenu.sections.forEach((section) =>
      section.items.forEach((item) => {
        if (item.itemId === itemId && item.type !== 'BUNDLE') isWithinBundle = true;
      }),
    );

    currentMenu.uncategorised.forEach((item) => {
      if (item.itemId === itemId && item.type !== 'BUNDLE') isWithinBundle = true;
    });

    return isWithinBundle;
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await deleteMenuItem(itemId);
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted "${name}".`,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (error) {
      if (isPartOfBundle()) {
        showSnackbar({
          status: SnackbarStatus.ERROR,
          message: error?.message,
        });
      } else {
        showSnackbar({
          status: SnackbarStatus.ERROR,
          message: 'We were unable to delete, please try again!',
        });
      }
      setIsRemovePromptVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMovePrompt = () => {
    openTray(
      'move-product',
      <MoveCategoryTrayContent
        key={getNextOpenId()}
        menuId={menuId}
        itemId={itemId}
        sectionId={sectionId}
        onClose={closeTray}
        onDirtyChange={setTrayDirty}
      />,
      {
        title: `Move "${name}" product?`,
        owner: itemId,
      },
    );
  };

  const reorderItem = async (newDisplayOrder: number) => {
    try {
      await reorderMenuItem({ itemId, newDisplayOrder });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  const openProductInNewTab = () => {
    window.open(redirectPath, '_blank', 'noopener,noreferrer');
  };

  const openProductPreview = () => {
    openTray('preview', <ProductPreviewTrayContent key={getNextOpenId()} itemId={parentItemId} />, {
      owner: itemId,
      title: 'Product Preview',
      footer: (
        <PrimaryButton size="full-width" onClick={openProductInNewTab}>
          Go to product
        </PrimaryButton>
      ),
    });
  };

  // Captured before the inner Link sees the click: react-router skips navigation once the
  // event is default-prevented. Only clicks on the link itself preview - the ellipsis menu
  // sits outside it - and modifier-clicks fall through so the product page can still open
  // in a new tab.
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const isNavigatingClick = !!(event.target as HTMLElement).closest('a');
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (!isNavigatingClick || isModifiedClick) return;

    event.preventDefault();
    openProductPreview();
  };

  const viewOnlyEllipsisOptions: IOption[] = [
    {
      id: 'view-product',
      name: 'View product',
      icon: ViewIcon,
      onOptionClick: openProductPreview,
    },
  ];

  const ellipsisOptions: IOption[] = [
    {
      id: 0,
      name: 'Go to product',
      icon: LinkIcon,
      onOptionClick: openProductInNewTab,
    },
    {
      id: 5,
      name: 'Preview product',
      icon: ViewIcon,
      onOptionClick: openProductPreview,
    },
  ];
  if (!isModifier) {
    ellipsisOptions.push({
      id: 1,
      name: 'Move to section',
      icon: MoveCategoryIcon,
      onOptionClick: handleMovePrompt,
      // A second move would replace the open one, so no card can start another.
      isDisabled: !multipleSections || trayKind === 'move-product',
    });
  }

  if (sectionId) {
    ellipsisOptions.push(
      {
        id: 2,
        name: 'Move left',
        icon: ArrowLeftIcon,
        onOptionClick: () => reorderItem(displayOrderLeft),
        isDisabled: displayOrderLeft === undefined,
        dataTestId: 'move-left',
        persistMenuOnClick: true,
      },
      {
        id: 3,
        name: 'Move right',
        icon: ArrowRightIcon,
        onOptionClick: () => reorderItem(displayOrderRight),
        isDisabled: displayOrderRight === undefined,
        dataTestId: 'move-right',
        persistMenuOnClick: true,
      },
    );
  }

  ellipsisOptions.push(
    {
      id: 'd-1',
      isDivider: true,
    },
    {
      id: 4,
      name: 'Remove',
      icon: Bin,
      onOptionClick: () => setIsRemovePromptVisible(true),
      hoverColour: 'tomato',
      // Whatever this card has open in the tray - a preview or a move form - refers to
      // this product, so removing it would leave the tray describing something gone.
      isDisabled: isTrayOwnedBy(itemId),
    },
  );
  return (
    <>
      {!isViewOnly && (
        <Prompt
          promptHeading={removePromptTitle}
          closePrompt={() => setIsRemovePromptVisible(false)}
          open={isRemovePromptVisible}
          buttonHandler={handleDelete}
          buttonText={'Remove'}
          buttonColour="tomato"
          backButton={true}
          size="small"
          isSubmitting={isSubmitting || isFetching}
        >
          {removeItemPromptSubTitle}
        </Prompt>
      )}
      <div onClickCapture={handleCardClick}>
        <ItemCard
          text={name}
          key={itemId}
          imageSrc={image}
          to={redirectPath}
          ellipsisOptions={isViewOnly ? viewOnlyEllipsisOptions : ellipsisOptions}
          subtext={consumerName}
          icon={SaladIcon}
          variant="quaternary"
          data-testid="card"
        />
      </div>
    </>
  );
};

export default memo(MenuItemCard);
